import { type ReactNode, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { useLocaleStore, LOCALE_BCP47 } from './store';
import ko from './messages/ko';
import ja from './messages/ja';
import zh from './messages/zh';

const MESSAGES = { ko, ja, zh } as const;

interface Props {
  children: ReactNode;
}

/**
 * Wraps the app in react-intl's IntlProvider.
 * Reads the current locale from the Zustand locale store (persisted to
 * localStorage). Switching locale re-renders the subtree with the new
 * message bundle; ko-KR is the default + fallback.
 */
export function LocaleProvider({ children }: Props) {
  const locale = useLocaleStore((s) => s.locale);

  const messages = useMemo(() => {
    // Merge ko (default) with the target locale so any untranslated key
    // falls back to Korean rather than showing a raw message ID.
    if (locale === 'ko') return MESSAGES.ko;
    return { ...MESSAGES.ko, ...MESSAGES[locale] };
  }, [locale]);

  return (
    <IntlProvider
      locale={LOCALE_BCP47[locale]}
      defaultLocale="ko-KR"
      messages={messages}
      onError={(err) => {
        // Suppress "Missing message" warnings in production; they are
        // expected for keys added after the last translation pass.
        if (err.code === 'MISSING_TRANSLATION') return;
        console.error('[intl]', err);
      }}
    >
      {children}
    </IntlProvider>
  );
}
